import React from "react";
import { useTenant } from "@/lib/TenantContext";
import { getTemplate, getTemplateChrome, isClassic, DEFAULT_TEMPLATE_ID } from "./registry";
import { TemplateShell } from "./shared/TemplateShell";

/**
 * ThemedPageLayout — the chrome of an INTERIOR public page (/reserver, /boutique,
 * /programmes, /parcours, /journal, /journal/:slug, /checkout/success).
 *
 * BUG-032: those seven pages imported `components/site/Navbar` and `components/site/Footer`
 * — CLASSIC's chrome — statically. A practice on any of the five themes therefore had a
 * themed homepage and six-plus Classic interior pages: crossing a single link swapped the
 * header, the footer, the palette and the type of the whole site. Combined with BUG-006 (no
 * themed header linked to an interior page at all) the effect was that picking a theme
 * silently downgraded everything except the landing page.
 *
 * So the chrome is RESOLVED, not imported: `tenant.site_template` -> registry
 * `getTemplateChrome()` -> { Header, Footer, mainClass }. The very same components the
 * homepage renders (SiteTemplateRenderer / the theme's own template file), so the two can
 * not drift again — there is one header per theme in the codebase, not two.
 *
 * The themed branch also re-applies the theme's TOKENS through TemplateShell (font links +
 * the `--tpl-*` CSS variables). Without it the themed header would render on an interior page
 * with every `var(--tpl-accent)` unresolved: an unstyled bar. Classic needs no shell — its
 * palette is Tailwind component classes in index.css.
 *
 * Page CONTENT is untouched: it stays whatever the page renders, including the top padding it
 * already carried to clear Classic's fixed navbar (the five themed headers are no taller).
 *
 * @param {React.ReactNode} children  the page's own sections
 * @param {string} [className]        appended to the theme's `mainClass`, for a page that
 *                                    needs one extra ground rule of its own
 */
export default function ThemedPageLayout({ children, className = "" }) {
  const { tenant } = useTenant();
  const templateId = tenant?.site_template || DEFAULT_TEMPLATE_ID;
  const { Header, Footer, mainClass } = getTemplateChrome(templateId);
  // Same source the homepage gates its sections on (SiteTemplateRenderer): a section the
  // practice switched off must not be advertised by an anchor in the header or the footer.
  const toggles = tenant?.section_toggles || {};

  const page = (
    <main
      data-testid="themed-page"
      data-site-template={templateId}
      className={`${mainClass} ${className}`.trim()}
    >
      <Header toggles={toggles} />
      {children}
      <Footer toggles={toggles} />
    </main>
  );

  if (isClassic(templateId)) return page;

  return (
    <TemplateShell template={getTemplate(templateId)} branding={tenant?.branding || {}}>
      {page}
    </TemplateShell>
  );
}
